import autobind from 'autobind-decorator';
import React, { PureComponent } from 'react';
// eslint-disable-next-line import/no-deprecated
import { clearInsuranceError, createInsurance, updateInsurance } from '~shared/actions/insurances';
import { type ModalType, changeModalStep, closeModal } from '~shared/actions/modal';
import { updateCashPay } from '~shared/actions/users';
import { getInferredInsuranceFormValues } from '~shared/features/insurances/helpers/getInferredInsuranceFormValues';
import { type Values } from '~shared/features/insurances/helpers/getInitialInsuranceFormValues';
import { getSelectedInsurance } from '~shared/features/insurances/selectors/getSelectedInsurance';
import {
  type CreateManualInsurancePayload,
  type CreatePhotoInsurancePayload,
  type UpdateManualInsurancePayload,
} from '~shared/features/insurances/types';
import getErrors from '~shared/features/ui/selectors/getErrors';
import getLoading from '~shared/features/ui/selectors/getLoading';
import { getCurrentUser } from '~shared/features/users/selectors/getCurrentUser';
import { getSelectedUser } from '~shared/features/users/selectors/getSelectedUser';
import { sendAnalyticsEvent } from '~shared/lib/analytics/src/actions';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { getInsuranceSource } from '~shared/lib/analytics/src/getInsuranceSource';
import { createEvent } from '~shared/lib/analytics/src/helper';
import { connect } from '~shared/store';
import { type ReduxStateShared } from '~shared/types';
import { type APIError } from '~shared/types/APIError';
import { type Insurance, type User } from '~shared/types/clients';
import InsuranceForm from './InsuranceForm';
import { removeEmptyValues } from './InsuranceFormElements';

type Props = {
  readonly closeModal: (modalName: ModalType) => void;
  // eslint-disable-next-line import/no-deprecated
  readonly createInsurance: (payload: CreateManualInsurancePayload | CreatePhotoInsurancePayload) => Promise<any>;
  // eslint-disable-next-line import/no-deprecated
  readonly updateInsurance: (payload: UpdateManualInsurancePayload) => Promise<any>;
  readonly insuranceError: APIError | null;
  readonly loading: boolean;
  readonly onCancel: () => void;
  readonly selectedInsurance: Insurance;
  readonly trackCashPaySet: () => void;
  readonly trackInsuranceAdded: (insurancePlanName: string) => void;
  readonly showPhotoForm?: () => void;
  readonly updateCashPay: (userID: number, isCashPayOnly: boolean) => Promise<{ success: boolean }>;
  readonly user: User;
};

class InsuranceFormContainer extends PureComponent<Props> {
  // @ts-expect-error error TS1270: Decorator function return type 'void | TypedPropertyDescriptor<unknown>' is not assignable to type 'void | (() => void)'
  @autobind
  onSubmit(values: Values) {
    const {
      closeModal,
      createInsurance,
      selectedInsurance,
      trackCashPaySet,
      trackInsuranceAdded,
      updateCashPay,
      updateInsurance,
      user,
    } = this.props;

    const onFinish = async (insurance: Insurance | false) => {
      if (!insurance) return;

      // update user's cash_pay_only when insurance is saved
      if (user.cash_pay_only) {
        const { success } = await updateCashPay(user.id, false);
        if (success) trackCashPaySet();
      }

      // track newly added insurance
      if (!selectedInsurance) {
        trackInsuranceAdded(insurance.insurance_plan_name ?? '');
      }

      closeModal('INSURANCE_MODAL');
    };

    const newInsuranceValues = getInferredInsuranceFormValues(values, user);

    if (selectedInsurance) {
      // eslint-disable-next-line promise/catch-or-return
      updateInsurance({
        id: selectedInsurance.id,
        ...removeEmptyValues(newInsuranceValues),
      }).then(onFinish);
    } else {
      // eslint-disable-next-line promise/catch-or-return
      createInsurance({
        user_id: user.id,
        ...removeEmptyValues(newInsuranceValues),
      }).then(onFinish);
    }
  }

  render() {
    const { loading, insuranceError, onCancel, selectedInsurance, showPhotoForm } = this.props;

    return (
      <InsuranceForm
        insuranceError={insuranceError}
        loading={loading}
        onCancel={onCancel}
        // eslint-disable-next-line @typescript-eslint/unbound-method
        onSubmit={this.onSubmit}
        selectedInsurance={selectedInsurance}
        showPhotoForm={showPhotoForm}
      />
    );
  }
}

const mapStateToProps = (state: ReduxStateShared) => {
  const errors = getErrors(state);
  const loading = getLoading(state);
  const selectedInsurance = getSelectedInsurance(state);
  const source = getInsuranceSource(state);

  const insuranceError =
    errors.fetchInsurancesError ||
    errors.createInsuranceError ||
    errors.updateInsuranceError ||
    errors.createPhotoInsuranceError;

  return {
    loading: loading.createInsuranceLoading || loading.updateInsuranceLoading,
    insuranceError,
    selectedInsurance,
    source,
    user: getSelectedUser(state) || getCurrentUser(state),
  };
};

const mapDispatchToProps = {
  // eslint-disable-next-line import/no-deprecated
  createInsurance,
  changeModalStep,
  clearInsuranceError,
  closeModal,
  sendAnalyticsEvent,
  updateCashPay,
  // eslint-disable-next-line import/no-deprecated
  updateInsurance,
};

// @ts-expect-error TS(7006): Parameter 'stateProps' implicitly has an 'any' type.
const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { source, ...restStateProps } = stateProps;
  const {
    changeModalStep: changeStep,
    clearInsuranceError: clearError,
    sendAnalyticsEvent,
    ...newDispatchProps
  } = dispatchProps;

  return {
    ...restStateProps,
    ...newDispatchProps,
    ...ownProps,
    showPhotoForm: () => {
      clearError();

      if (ownProps.showPhotoForm) {
        ownProps.showPhotoForm();
      } else {
        changeStep(1, 'INSURANCE_MODAL');
      }
    },
    trackCashPaySet: () => {
      sendAnalyticsEvent(
        createEvent(EVENTS.CASH_PAY_ONLY_SET, {
          source,
          'new value': false,
        }),
      );
    },
    trackInsuranceAdded: (insurancePlanName: string) => {
      sendAnalyticsEvent(
        createEvent(EVENTS.INSURANCE_ADDED, {
          type: 'manual',
          source,
          entity_name: insurancePlanName,
        }),
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(InsuranceFormContainer);
